/**
 * My day — the salesperson's home.
 *
 * Start Day / Close Day is a WORKDAY BOUNDARY for target calculation, not
 * attendance. The client excluded presence tracking, so no location is captured
 * here — and the screen says so plainly, because a field team that suspects it
 * is being tracked stops using the app honestly.
 *
 * One session per calendar day. If Close Day is forgotten, the server closes it
 * at midnight and marks it as such.
 */

import { useEffect, useMemo, useState, useRef, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../components/Shell';
import { Greeting } from '../../components/Greeting';
import {
  AuthImage, Banner, Button, Card, Chip, ConfirmSheet, Segment, Select, Sheet, Skeleton,
  Switch, TextArea, TextInput, Tile, useToast,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus, usePoll } from '../../lib/useApi';
import { useT } from '../../lib/i18n';
import { api, ApiError, apiErrorText, postFile } from '../../lib/api';
import { NoteCounter, noteTotalPaise, toDenominations, type NoteCounts } from '../../components/NoteCounter';
import { getCoords, type CompressedImage } from '../../lib/camera';
import { CameraSheet } from '../../components/Camera';
import { useAuth } from '../../lib/auth';
import { money, timeShort } from '../../lib/format';

interface TargetBlock {
  period: string;
  targets: {
    id: string; dimension_type: string;
    target_quantity: number; target_value_paise: number; target_customer_count: number;
    target_boxes: number;
    category_name: string | null; area_name: string | null; store_type_name: string | null;
    /** Measured on this target's own dimension — see api field.ts achievementFor. */
    achieved: { boxes: number; quantity: number; valuePaise: number; customerCount: number };
    achievementPercent: {
      boxes: number | null;
      quantity: number | null; value: number | null; customers: number | null };
  }[];
  achieved: { quantity: number; valuePaise: number; customerCount: number };
  achievementPercent: { quantity: number | null; value: number | null; customers: number | null } | null;
}

interface DayState {
  started: boolean;
  closed?: boolean;
  sessionId?: string;
  sessionDate: string;
  startedAt?: string;
  closedAt?: string | null;
  closeType?: string | null;
  message?: string;
  beat?: { beatId: string; beatName: string; beatCode: string; outletCount: number } | null;
  startLocation?: { latitude: number; longitude: number; accuracyM: number | null; mapsUrl: string } | null;
  /** The driver's standing van (0065), so the start form pre-selects it. */
  defaultVehicleId?: string | null;
  beatChangePending?: { requestedBeat: string; requestedAt: string } | null;
  deliveryGate?: { enforced: boolean; hasLoad: boolean; accepted: boolean; officeApproved: boolean; locked: boolean } | null;
  /** This van has a load-out recorded today (#25). */
  vanLoaded?: boolean;
  /** Whether this person self-loads (field_agent) vs the admin loads for them. */
  canLoadOwnVan?: boolean;
  van?: { vehicleId: string; registration: string | null } | null;
  targets?: TargetBlock | null;
  progress?: {
    outletsVisited: number; outletsSkipped: number; customersAdded: number;
    customersUpdated?: number;
    ordersBooked: number; orderValuePaise: number; orderQuantity: number;
    collectionsCount: number; collectionsAmountPaise: number;
    collectionsCashPaise?: number; collectionsUpiPaise?: number;
    collectionsAdvancePaise?: number; collectionsBalancePaise?: number;
    noOrderCount: number;
  };
  autoCloseAt?: string | null;
  note?: string;
  /**
   * Today's sales by product category.
   *
   * Running, not only at close: a salesperson who can see at 2pm that one
   * category is at zero can still do something about it. Optional because an
   * older server build does not send it.
   */
  byCategory?: CategoryRow[];
  /**
   * Phase 3 — the load sheet the driver has not counted on his van yet.
   *
   * Non-null when a sheet exists for today's driver, has not been through
   * the van-confirm step, and is in a 'loaded' or 'accepted' status. Null
   * for a salesperson, for a driver without a sheet, or once he has
   * confirmed. The server computes it (see field.ts `pendingVanConfirm`)
   * so the UI does not have to decide what to prompt about.
   */
  vanConfirm?: { loadSheetId: string; loadSheetNumber: string; skuCount: number } | null;
}

/** `/api/stock/load-sheets/:id/van-prefill` — what the confirm sheet opens with. */
interface VanPrefillData {
  loadSheetNumber: string;
  status: string;
  driverAccepted: boolean;
  alreadyConfirmed: boolean;
  vanConfirmedAt: string | null;
  hasVanVariance: boolean;
  photoUrl: string | null;
  items: {
    productId: string;
    skuCode: string;
    productName: string;
    category: string | null;
    packUom: string | null;
    packQty: number | null;
    loadedPieces: number;
    loadedPets: number;
    confirmedPieces: number | null;
    confirmedPets: number | null;
    variancePieces: number | null;
    variancePets: number | null;
    reason: string | null;
    // Phase 4 — the running figure, served per SKU in pets.
    deliveredPieces: number;
    deliveredPets: number;
    extraBoxSoldPieces: number;
    returnedInPieces: number;
    remainingPieces: number;
    remainingPets: number;
  }[];
}

interface CategoryRow {
  categoryId: string | null;
  category: string;
  quantity: number;
  valuePaise: number;
  display: { value: string };
}

/**
 * One category as a compact tile — a colour-keyed dot and name, the
 * achieved / target figure in large type, and a thin progress bar with its
 * percent. Used two-up so the four categories fit in two rows instead of
 * eight stacked full-width rows, which is what the dashboard asked for.
 */
function CatTile({
  name, color, achieved, target, pct, set, badge,
}: {
  name: string; color: string; achieved: number; target: string;
  pct: number; set: boolean; badge?: string | null;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--line-2)', borderRadius: 12,
        padding: '11px 12px', display: 'flex', flexDirection: 'column', gap: 8,
        background: 'var(--surface, #fff)',
      }}
    >
      <div className="row" style={{ gap: 7, alignItems: 'center', minWidth: 0 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flex: '0 0 auto' }} />
        <span className="t-xs t-semi" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        {badge && <Chip tone="warn">{badge}</Chip>}
      </div>
      <div style={{ lineHeight: 1.1 }}>
        <span style={{ fontSize: 20, fontWeight: 700 }}>{achieved}</span>
        <span className="t-xs t-muted"> / {target}</span>
      </div>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'var(--line-2)', overflow: 'hidden' }}>
          <span style={{ display: 'block', height: '100%', width: `${set ? Math.max(pct, 3) : 0}%`, background: color, borderRadius: 999 }} />
        </div>
        <span className="t-xs t-muted" style={{ flex: '0 0 auto', minWidth: 30, textAlign: 'right' }}>
          {set ? `${pct}%` : '—'}
        </span>
      </div>
    </div>
  );
}

/**
 * The four core categories, with the regexes that bucket a raw BC category
 * (or a product name) into them. `emergy` is tolerated because Business
 * Central has the Energy category misspelled "EMERGY DRINK".
 *
 * MODULE SCOPE, not inside the component. It was a component-local `const`
 * declared below the first render-time hooks; the Phase 4 `vanTotalsByCat`
 * memo reads it during render, BEFORE that declaration line runs, which is
 * a temporal-dead-zone ReferenceError that blanked the driver dashboard the
 * moment van data loaded. Static data has no reason to live in the component
 * anyway — hoisting it fixes the crash and stops re-allocating the array
 * (and its regexes) on every render.
 */
const STD_CATS = [
  { name: 'CAMPA Water', short: 'Water', re: /water/i, color: '#2b82c9', sampleMonthly: 18 },
  { name: 'COLD Drink', short: 'Cold', re: /cola|cold|soft/i, color: '#c0392b', sampleMonthly: 22 },
  { name: 'CAMPA Juice', short: 'Juice', re: /juice/i, color: '#e08a1e', sampleMonthly: 12 },
  // `emergy` too — Business Central has it misspelled "EMERGY DRINK".
  { name: 'ENERGY Drink', short: 'Energy', re: /energy|emergy/i, color: '#6d3fd1', sampleMonthly: 8 },
];

/** "X Pets & Y Pieces" — the one unit the Stock-in-Van tables speak. */
function petsPieces(pets: number, pieces: number): string {
  return `${pets} Pets & ${pieces} Pieces`;
}

/**
 * Split a piece count into whole pets + loose pieces — the LOSSLESS conversion.
 *
 * This is the only correct way to show a quantity in two units: pets are
 * floor(pieces / pack) and the remainder is loose pieces, so pets × pack + loose
 * always equals the pieces exactly. The van-confirm screen used to show
 * round(pieces / pack) with no loose remainder and then multiply the whole-pet
 * count back, which both displayed figures that did not reconcile (e.g. "4 Pets
 * & 50 Pieces" for pack 12) and manufactured a false variance whenever the load
 * was not an exact number of packs. packQty of 1 (or none) means pieces are the
 * only unit. The admin counting screens already do exactly this.
 */
function splitPP(pieces: number, packQty: number | null): { pets: number; loose: number } {
  const pack = packQty && packQty > 1 ? packQty : 1;
  return { pets: Math.floor(pieces / pack), loose: pieces % pack };
}

// Shared styling for the four-column Stock-in-Van table (Phase 6). Kept as
// plain inline style objects so the table renders the same on the dashboard
// and the Stock page without a new stylesheet.
const VAN_TABLE: CSSProperties = {
  width: '100%', borderCollapse: 'collapse', minWidth: 540, fontSize: 13,
};
const VAN_TH: CSSProperties = {
  textAlign: 'right', padding: '8px 10px', fontSize: 11, fontWeight: 700,
  color: 'var(--ink-2, #555)', borderBottom: '1px solid var(--line-2)', whiteSpace: 'nowrap',
};
const VAN_TH_LEFT: CSSProperties = { ...VAN_TH, textAlign: 'left' };
const VAN_TD: CSSProperties = {
  textAlign: 'right', padding: '9px 10px', borderBottom: '1px solid var(--line-2)',
  whiteSpace: 'nowrap', fontWeight: 600,
};
const VAN_TD_LEFT: CSSProperties = {
  ...VAN_TD, textAlign: 'left', fontWeight: 700, overflowWrap: 'anywhere', whiteSpace: 'normal',
};

export default function Day() {
  const t = useT();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState<
    { loadSheetId: string; loadSheetNumber: string; skuCount: number } | null
  >(null);
  const toast = useToast();
  // A hard stop the driver cannot fix from here — chiefly a lapsed/missing
  // driving licence (the day-start gate). A toast vanishes; this keeps the real
  // reason on the start card until the office fixes it and the next attempt
  // clears it, so the driver is never left staring at a blank "please correct".
  const [startBlock, setStartBlock] = useState<string | null>(null);

  const day = useQuery<DayState>('/api/field/day/current');
  useRefreshOnFocus(() => { void day.refetch(); });
  // The staff member's own photo for the profile card — live, so a fresh upload
  // shows without re-login. Falls back to initials when none is set.
  const meProfile = useQuery<{ photoKey: string | null }>('/api/staff/me');
  const photoKey = meProfile.data?.photoKey ?? null;

  // The current day's data, aliased early because several Phase 3 hooks
  // (van-confirm prompt, prefill query, Stock in Van card) read from it
  // before React's own render pass does. The old call site below used to
  // declare `const d = day.data` right before the JSX; moving it up keeps
  // every reference to `d` consistent and TypeScript stops complaining
  // about use-before-declaration.
  const d = day.data;

  const { user } = useAuth();
  // The combined field agent runs the driver app — van, stock, deliveries — AND
  // books orders. Treating them as a driver here lights up the van flow and all
  // six quick-action tiles; the van-mode toggle and the "riding with" picker are
  // hidden for them (they are both roles in one person, nobody to pair with).
  const isFieldAgent = user?.role === 'field_agent';
  const isDriver = user?.role === 'driver' || isFieldAgent;
  const [vehicleId, setVehicleId] = useState('');

  // Solo rep vs van sales (with a driver). Van sales has no backend pairing
  // yet, so the van-stock figures are marked sample — but the driver is chosen
  // from the real list of drivers who have checked in with a van today.
  const [mode, setMode] = useState<'solo' | 'van'>('solo');
  const [vanDriverVehicleId, setVanDriverVehicleId] = useState('');
  // The salesperson a DRIVER is riding with in "with salesperson" mode — the
  // mirror of vanDriverVehicleId, which is who a SALESPERSON rides with.
  const [vanSalespersonId, setVanSalespersonId] = useState('');
  const [win, setWin] = useState<'today' | 'month'>('today');

  // A driver picks a van; a salesperson in van-sales mode picks the driver they
  // are riding with. Both read the same today's-vehicles list.
  const vehicles = useQuery<{
    items: { id: string; registration_number: string; vehicle_type: string | null;
             taken_by: string | null; driver_name: string | null }[];
  }>(isDriver || mode === 'van' ? '/api/admin/masters/vehicles-today' : null);

  // Pre-select the driver's standing van (allocated by the admin, migration
  // 0065) so he does not pick one every morning. Only when he has not already
  // chosen; he can still change it.
  useEffect(() => {
    const def = day.data?.defaultVehicleId;
    if (def && !vehicleId) setVehicleId(def);
  }, [day.data?.defaultVehicleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The other half of the swap: a DRIVER pairing with a salesperson needs the
  // salesperson list, exactly as a salesperson pairing with a driver needs the
  // van list above. Field-accessible, same as vehicles-today.
  const salespeople = useQuery<{ items: { id: string; name: string; code: string | null }[] }>(
    isDriver && mode === 'van' ? '/api/admin/masters/salespersons-for-order' : null);

  /**
   * THE DRIVER'S OWN TARGET, ON HIS OWN SCREEN.
   *
   * 6 Oct: "it should be auto update to the sales person and this same
   * target point we have to implement for drivers also."
   *
   * Fetched separately because `/day/current` builds its targets from a
   * salesperson profile, which a driver has not got. Nothing is set here —
   * the daily figure is the month ÷ 30, worked out server-side — so this is
   * a read that needs no action from anybody.
   */
  const driverTarget = useQuery<{
    driver: {
      month: string; date: string; targetBoxes: number; dailyTargetBoxes: number;
      monthly: { delivered: number; sold: number; totalBoxes: number; percent: number | null };
      today: { delivered: number; sold: number; totalBoxes: number; percent: number | null };
    } | null;
    // A salesperson's per-category PET targets, so the dashboard can show the
    // real goal the admin set — not just the store-reach count. Null for a
    // driver (whose target is overall boxes, shown in the boxes card below).
    daily: TargetBlock | null;
    monthly: TargetBlock | null;
  }>('/api/field/targets');

  const [busy, setBusy] = useState(false);
  // Idempotency key for the handover declared at close (item #3).
  const closeUuid = useRef<string | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  // Day-close confirmations (client, 24 Oct): the collector declares the cash he
  // is handing over, note by note (the same counter as Collect → "tap to hand
  // over"), and a driver also acknowledges the stock left in the van. Reset each
  // time the sheet opens so a prior close cannot carry a stale count.
  const [closeNotes, setCloseNotes] = useState<NoteCounts>({});
  const [ackVan, setAckVan] = useState(false);
  useEffect(() => { if (closeOpen) { setCloseNotes({}); setAckVan(false); } }, [closeOpen]);

  // The full cash the collector is holding — today's collection PLUS any balance
  // carried from earlier days — so the close screen shows the total to hand over,
  // not just today's figure.
  const cashMine = useQuery<{ amountPaise: number; count: number }>(
    '/api/payments/cash-in-hand/mine',
  );

  // Beat change request — the picker sheet, and the beats to choose from
  // (fetched only while the sheet is open).
  const [beatSheetOpen, setBeatSheetOpen] = useState(false);
  const [beatChoice, setBeatChoice] = useState('');
  const [beatReason, setBeatReason] = useState('');
  const [beatBusy, setBeatBusy] = useState(false);
  const beatOptions = useQuery<{ items: { id: string; name: string; code: string }[] }>(
    beatSheetOpen ? '/api/field/beats/options' : null,
  );

  async function requestBeatChange() {
    if (!beatChoice) { toast.show('Pick the beat you want.', 'danger'); return; }
    if (beatReason.trim().length < 3) { toast.show('Give a reason for the change.', 'danger'); return; }
    setBeatBusy(true);
    try {
      await api.post('/api/field/beat/change-request', {
        requestedBeatId: beatChoice,
        reason: beatReason.trim(),
      });
      setBeatSheetOpen(false);
      setBeatChoice('');
      setBeatReason('');
      toast.show('Beat change requested — waiting for admin approval.', 'ok');
      await day.refetch();
    } catch (err) {
      toast.show(apiErrorText(err, 'Could not request the change'), 'danger');
    } finally {
      setBeatBusy(false);
    }
  }

  /**
   * STARTING THE DAY NOW TAKES A SELFIE, A LOCATION FIX AND — FOR A DRIVER —
   * A VEHICLE.
   *
   * The server has required the first two for salespeople since migration
   * 0034 and this screen never sent them, so `POST /api/field/day/start` had
   * been returning a validation error for every salesperson since that deploy:
   * the feature existed on one side of the wire only.
   *
   * All three are captured BEFORE the call, and the call is not made unless
   * every one of them succeeded. A partial start — a session row with no
   * selfie — is worse than no start, because it looks like attendance evidence
   * and is not.
   */
  /**
   * STARTING THE DAY, IN TWO STEPS INSTEAD OF ONE BLOCKING CALL.
   *
   * The old version did everything inside the button press: location, then
   * camera, then upload, then the API. That is why it "does not click and
   * takes time" — the handler sat awaiting a GPS fix for up to ten seconds
   * with no feedback, and if the OS was slow the button looked dead. Then it
   * opened a file picker, which on this phone shows the gallery.
   *
   * Now: the button takes the location, shows progress while it does, and
   * opens a REAL camera. The day is started when the shutter fires, from
   * `finishStart` below. Nothing silently waits.
   */
  const [coords, setCoords] = useState<{ latitude: number; longitude: number; accuracyM: number } | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [locating, setLocating] = useState(false);

  /**
   * Phase 3 — van-start confirmation. One sheet, one photo, per-SKU pet
   * stepper + optional reason. The driver cannot be prompted for a sheet
   * he does not have, so every piece of this is gated on `d.vanConfirm`
   * being non-null — which the server computes.
   *
   * `vanConfirmCoords` is captured BEFORE the camera opens, same shape as
   * day-start. Capturing it in the submit handler would make the GPS wait
   * the shutter, which this project has learned not to do.
   */
  const [vanConfirmOpen, setVanConfirmOpen] = useState(false);
  const [vanConfirmBusy, setVanConfirmBusy] = useState(false);
  const [vanConfirmCamera, setVanConfirmCamera] = useState(false);
  const [vanConfirmLocating, setVanConfirmLocating] = useState(false);
  const [vanConfirmCoords, setVanConfirmCoords] = useState<
    { latitude: number; longitude: number; accuracyM: number } | null
  >(null);
  // Per-SKU confirmed count, held as whole pets + loose pieces (strings, so the
  // field can be cleared while typing) — lossless, matching the admin counting UI.
  const [vanConfirmCounts, setVanConfirmCounts] = useState<Record<string, { pets: string; pieces: string }>>({});
  const [vanConfirmReasons, setVanConfirmReasons] = useState<Record<string, string>>({});

  /**
   * THE WHOLE VAN, AGGREGATED ACROSS EVERY ACTIVE SHEET.
   *
   * Phase 5-fix. The Stock in Van card used to read ONE load sheet via
   * /van-prefill. A driver can carry more than one active sheet at a time
   * (four Campa Water sheets on one van, in production), and a delivery that
   * reduced one sheet never moved a figure read from another — so the card
   * showed stale stock after a real delivery. /api/field/van sums starting
   * and remaining across every accepted/in_transit sheet, per product, which
   * is what the van physically holds. One source for the display; the confirm
   * sheet below still reads the single sheet it is confirming.
   *
   * Gated on isDriver: the endpoint returns an empty van for a non-driver.
   */
  const van = useQuery<{
    hasVan: boolean;
    allConfirmed: boolean;
    pendingConfirm: number;
    items: {
      productId: string; skuCode: string; productName: string; category: string | null;
      packUom: string | null; packQty: number | null;
      startingPieces: number; startingPets: number; startingLoose: number;
      deliveredPieces: number; deliveredPets: number; deliveredLoose: number;
      remainingPieces: number; remainingPets: number; remainingLoose: number;
    }[];
  }>(isDriver ? '/api/field/van' : null);

  /**
   * Counts for the two delivery tiles on a driver's My Day — his own pending
   * deliveries and what he has delivered today. Same sources the Orders screen
   * and the driver's "Orders Pending for Delivery" list use, so the tile and
   * the list it opens can never disagree. Polled so they track the round.
   */
  const myPending = useQuery<{ items: unknown[] }>(isDriver ? '/api/orders/assigned' : null);
  const myDelivered = useQuery<{ items: unknown[] }>(isDriver ? '/api/orders/delivered-today' : null);
  usePoll(myPending.refetch, 15000, isDriver);
  usePoll(myDelivered.refetch, 15000, isDriver);
  useRefreshOnFocus(() => { void myPending.refetch(); void myDelivered.refetch(); });
  const myPendingCount = myPending.data?.items.length ?? 0;
  const myDeliveredCount = myDelivered.data?.items.length ?? 0;

  /**
   * The per-sheet prefill — ONLY for the confirm sheet, keyed on the one
   * unconfirmed sheet the server flagged in `d.vanConfirm`. The display no
   * longer uses it (the aggregate `van` above does). Fetched only when there
   * is a sheet to confirm.
   */
  const vanPrefill = useQuery<VanPrefillData>(
    d?.vanConfirm?.loadSheetId
      ? `/api/stock/load-sheets/${d.vanConfirm.loadSheetId}/van-prefill`
      : null,
    { deps: [d?.vanConfirm?.loadSheetId] },
  );

  // Seed the per-SKU count state when the confirm prefill lands. The stepper
  // defaults to the driver's own count if he already made one, else Admin's
  // loaded figure.
  useEffect(() => {
    if (!vanPrefill.data) return;
    const next: Record<string, { pets: string; pieces: string }> = {};
    for (const i of vanPrefill.data.items) {
      // Default to the driver's own count if he already made one, else what Admin
      // loaded — working in PIECES (the exact figure) and splitting into whole
      // pets + loose pieces, so nothing is dropped and the count reconciles.
      const pieces = i.confirmedPieces ?? i.loadedPieces;
      const s = splitPP(pieces, i.packQty);
      next[i.productId] = { pets: String(s.pets), pieces: String(s.loose) };
    }
    setVanConfirmCounts(next);
  }, [vanPrefill.data]);

  /**
   * Per-category van totals, in PETS and loose PIECES, from the aggregate.
   *
   * A Pet is a pack/box/case; a Piece is one bottle or can. When part of a
   * pack is sold, the remainder shows as loose pieces: 1 Pet of 12 with 6
   * sold reads "0 Pets & 6 Pieces". Per SKU the server already split remaining
   * into whole pets and loose pieces against that SKU's pack size; here we sum
   * those across the SKUs in each of the four dashboard categories, plus the
   * starting pets the row is measured against.
   *
   * STD_CATS regexes group raw category names (or the product name as a
   * fallback) to the four buckets; an unmatched product is not shown.
   */
  const vanTotalsByCat = useMemo(() => {
    const totals = new Map<string, {
      loadedPets: number; loadedPieces: number;
      deliveredPets: number; deliveredPieces: number;
      balancePets: number; balancePieces: number;
    }>();
    for (const item of van.data?.items ?? []) {
      let matched: string | null = null;
      for (const c of STD_CATS) {
        if (item.category && c.re.test(item.category)) { matched = c.name; break; }
        if (c.re.test(item.productName)) { matched = c.name; break; }
      }
      if (!matched) continue;
      const prev = totals.get(matched) ?? {
        loadedPets: 0, loadedPieces: 0,
        deliveredPets: 0, deliveredPieces: 0,
        balancePets: 0, balancePieces: 0,
      };
      prev.loadedPets += item.startingPets;
      prev.loadedPieces += item.startingLoose;
      prev.deliveredPets += item.deliveredPets;
      prev.deliveredPieces += item.deliveredLoose;
      prev.balancePets += item.remainingPets;
      prev.balancePieces += item.remainingLoose;
      totals.set(matched, prev);
    }
    return totals;
  }, [van.data]);

  /**
   * Open the van-confirm sheet.
   *
   * Pattern mirrored from `startDay`: location first (because the OS can
   * refuse it), then the sheet renders and offers the camera button. A
   * driver who denies location sees a toast and nothing silently fails.
   */
  async function openVanConfirm() {
    if (!d?.vanConfirm) return;
    setVanConfirmLocating(true);
    try {
      const fix = await getCoords({ highAccuracy: true });
      if (!fix) {
        toast.show(
          'Your location is needed to confirm the van. Allow location access and try again.',
          'danger',
        );
        return;
      }
      setVanConfirmCoords(fix);
      setVanConfirmOpen(true);
    } finally {
      setVanConfirmLocating(false);
    }
  }

  /**
   * Submit — called once the camera has returned a photo.
   *
   * Pets × packQty for every item, so the server receives PIECES (same unit
   * the ledger uses). A pet count that cannot be converted (packQty missing)
   * is sent as-is — the server treats it as pieces, which is what a pack of
   * one piece is anyway.
   */
  async function submitVanConfirm(photo: CompressedImage) {
    if (!d?.vanConfirm || !vanConfirmCoords || !vanPrefill.data) return;
    setVanConfirmBusy(true);
    try {
      const uploaded = await postFile<{ mediaId: string }>(
        '/api/media/upload', photo.blob, 'van-confirm.jpg',
        // 'pod_photo' — the same retention/auth class as a delivery photo,
        // and the one allowed value that fits (see HANDOVER §5 Phase 3).
        // The server flips entity_type to 'load_sheet' on confirm.
        { purpose: 'pod_photo' },
      );
      const counts = vanPrefill.data.items.map((i) => {
        const pack = i.packQty && i.packQty > 1 ? i.packQty : 1;
        const c = vanConfirmCounts[i.productId];
        // PIECES = pets × pack + loose. Lossless: what the driver counted is sent
        // exactly, so confirming the loaded figure produces zero variance (the old
        // round-trip could not, and invented a shortage on every non-pack load).
        const pieces = (Number(c?.pets || 0) * pack) + Number(c?.pieces || 0);
        return {
          productId: i.productId,
          confirmedQuantity: pieces,
          reason: vanConfirmReasons[i.productId]?.trim() || undefined,
        };
      });
      await api.post(`/api/stock/load-sheets/${d.vanConfirm.loadSheetId}/van-confirm`, {
        selfieMediaId: uploaded.mediaId,
        latitude: vanConfirmCoords.latitude,
        longitude: vanConfirmCoords.longitude,
        accuracyM: vanConfirmCoords.accuracyM,
        counts,
      });
      toast.show('Van stock confirmed', 'ok');
      setVanConfirmOpen(false);
      void day.refetch();
      void vanPrefill.refetch();
      void van.refetch();
    } catch (err) {
      toast.show(apiErrorText(err, 'Could not confirm the van'), 'danger');
    } finally {
      setVanConfirmBusy(false);
    }
  }

  async function startDay() {
    if (isDriver && !vehicleId) {
      toast.show('Choose the vehicle you are driving today.', 'danger');
      return;
    }

    // The location first: it is the one the OS can refuse, and asking somebody
    // to photograph themselves before telling them the day cannot start wastes
    // their time.
    setLocating(true);
    try {
      const fix = await getCoords({ highAccuracy: true });
      if (!fix) {
        toast.show(
          'Your location is needed to start the day. Allow location access and try again.',
          'danger',
        );
        return;
      }
      setCoords(fix);
      setCameraOpen(true);
    } finally {
      setLocating(false);
    }
  }

  /** Called by the camera sheet once a frame has been taken. */
  async function finishStart(photo: CompressedImage) {
    if (!coords) return;
    setBusy(true);
    try {
      const uploaded = await postFile<{ mediaId: string }>(
        '/api/media/upload', photo.blob, 'selfie.jpg', { purpose: 'attendance_selfie' },
      );

      await api.post('/api/field/day/start', {
        selfieMediaId: uploaded.mediaId,
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracyM: coords.accuracyM,
        ...(isDriver ? { vehicleId } : {}),
      });
      setStartBlock(null);
      toast.show(t('day.startDay'), 'ok');
      void day.refetch();
    } catch (err) {
      const text = apiErrorText(err, t('app.somethingWrong'));
      toast.show(text, 'danger');
      // A licence block is a hard stop the driver can't resolve themselves, so
      // keep it on the card (not just a toast). Any other reason is transient
      // enough that the toast is the right weight.
      setStartBlock(err instanceof ApiError && err.fields?.licence ? err.fields.licence : null);
    } finally {
      setBusy(false);
    }
  }

  async function closeDay() {
    const totalCash = cashMine.data?.amountPaise ?? 0;
    const declared = noteTotalPaise(closeNotes);

    // A driver must confirm the van stock first.
    if (isDriver && !ackVan) {
      toast.show('Confirm the stock left in your van before closing.', 'danger');
      return;
    }
    // If the collector is holding cash, closing declares the handover note by
    // note (the same count the admin confirms). A short count needs a reason.
    if (totalCash > 0) {
      if (declared <= 0) {
        toast.show('Count the notes you are handing over before closing.', 'danger');
        return;
      }
      if (declared < totalCash && !remarks.trim()) {
        toast.show('You are handing over less than you hold — add a reason below.', 'danger');
        return;
      }
    }

    setBusy(true);
    try {
      // Declare the cash handover as part of closing. If one is already pending
      // (declared earlier today), the cash is accounted for, so close anyway;
      // any other failure stops the close so nothing is lost silently.
      if (totalCash > 0) {
        try {
          if (!closeUuid.current) closeUuid.current = crypto.randomUUID();
          await api.post('/api/payments/settlements', {
            declaredAmountPaise: declared,
            denominations: toDenominations(closeNotes),
            remarks: remarks.trim() || undefined,
            clientUuid: closeUuid.current,
          });
          closeUuid.current = null;
        } catch (err) {
          if (!(err instanceof ApiError && /awaiting confirmation/i.test(err.message))) {
            toast.show(err instanceof ApiError ? err.message : 'Could not declare the handover', 'danger');
            setBusy(false);
            return;
          }
        }
      }

      const result = await api.post<{
        summary: Record<string, unknown>;
        byCategory: CategoryRow[];
        pendingCount: { loadSheetId: string; loadSheetNumber: string; skuCount: number } | null;
      }>(
        '/api/field/day/close', { remarks: remarks.trim() || undefined },
      );
      toast.show(t('day.closeDay'), 'ok');
      setCloseOpen(false);
      /**
       * The van he has not counted back in.
       *
       * Requested 3 Oct: "auto counting should be populate after day close by
       * driver". The count has always existed and has always been prefilled
       * with the expected residual — on a button two screens away that
       * nothing pointed at. Closing the day is the moment he is standing at
       * the godown with the van, so it is the moment to offer it.
       */
      setPendingCount(result.pendingCount);
      void cashMine.refetch();
      /**
       * `byCategory` comes back on this response too, and is deliberately not
       * stored: the running "Today by category" card below has no `closed`
       * guard, so it stays on screen afterwards showing the same figures. A
       * second copy in component state would be two sources for one number,
       * and the stale one would be the one still rendered tomorrow morning.
       */
      void day.refetch();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  if (day.loading && !day.data) {
    return <Page title={t('day.title')}><Skeleton rows={3} height={90} /></Page>;
  }

  const p = d?.progress;

  // Real performance figures from the daily target block and today's progress.
  const firstTarget = d?.targets?.targets?.[0];
  const petsSold = firstTarget?.achieved.boxes ?? 0;

  // Billed-outlets reach, always shown for the four core categories so the
  // section never collapses to nothing. Each bar is filled from the real
  // per-category target when one matches by name (target_customer_count is the
  // store goal, achieved.customerCount is how many are billed); where no target
  // is set the bar falls back to a sample goal and is marked. The server hands
  // a DAILY goal (monthly ÷ days), so Monthly multiplies it back up.
  const DAYS = 30;
  const targetsList = d?.targets?.targets ?? [];
  const catReach = STD_CATS.map((c) => {
    const hit = targetsList.find((x) => x.category_name && c.re.test(x.category_name));
    const billed = hit?.achieved.customerCount ?? 0;
    const target = win === 'month'
      ? (hit ? hit.target_customer_count * DAYS : c.sampleMonthly)
      : (hit ? hit.target_customer_count : Math.max(1, Math.round(c.sampleMonthly / DAYS)));
    // Pets (boxes) sold in this category today, for the Pets-sold badges.
    const pets = hit?.achieved.boxes ?? 0;
    return { name: c.name, short: c.short, color: c.color, billed, target, sample: !hit, pets };
  });

  /**
   * THE PET TARGET THE ADMIN ACTUALLY SET, reflected on the rep's own screen.
   *
   * Targets are set per category in PETs (boxes); until now the dashboard only
   * showed store reach, so a salesperson could not see the 3000-PET Campa Water
   * goal set for them. This reads the real per-category block — monthly or the
   * month ÷ 30 daily figure, following the toggle — so achieved / target match
   * the admin's Targets screen to the number.
   */
  const petBlock = win === 'month' ? driverTarget.data?.monthly : driverTarget.data?.daily;
  const petCats = STD_CATS.map((c) => {
    const hit = (petBlock?.targets ?? []).find(
      (x) => x.dimension_type === 'category' && x.category_name && c.re.test(x.category_name),
    );
    const target = hit?.target_boxes ?? 0;
    return {
      name: c.name,
      short: c.short,
      color: c.color,
      achieved: hit?.achieved.boxes ?? 0,
      target,
      pct: hit?.achievementPercent.boxes ?? 0,
      set: target > 0,
    };
  });

  /**
   * The rows for the "Pets sold" card — the four category PET targets as
   * full-width slide-bars (the same style as the admin Targets screen). A
   * salesperson is measured per category; a driver usually has a single OVERALL
   * boxes target, so when no category target is set we show one "Overall" bar
   * instead of four empty ones.
   */
  const overallPetTarget = win === 'month'
    ? (driverTarget.data?.driver?.targetBoxes ?? 0)
    : (driverTarget.data?.driver?.dailyTargetBoxes ?? 0);
  const petBars = petCats.some((c) => c.set)
    ? petCats.map((c) => ({ name: c.name, achieved: c.achieved, target: c.target, pct: c.pct, set: c.set }))
    : overallPetTarget > 0
      ? [{
          name: 'Overall',
          achieved: petsSold,
          target: overallPetTarget,
          pct: overallPetTarget > 0 ? Math.round((100 * petsSold) / overallPetTarget) : 0,
          set: true,
        }]
      : petCats.map((c) => ({ name: c.name, achieved: c.achieved, target: c.target, pct: c.pct, set: c.set }));
  /** Red under 70%, amber 70–99%, green at/over 100% — matches the admin screen. */
  const petBarTone = (pct: number, set: boolean) =>
    !set ? 'danger' : pct >= 100 ? 'ok' : pct >= 70 ? 'warn' : 'danger';

  const qaStyle: CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 7, alignItems: 'flex-start',
    padding: 14, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'var(--ink)',
  };
  // Colour the WHOLE quick-action card, not just its icon — the card body is what
  // fills the driver/salesman landing, so the earlier icon-only colour left the
  // screen reading white. Each card takes its action's hue (tinted fill + a left
  // colour bar); the icon chip flips to white so it still stands out on the tint.
  const qaTile = (hue: 'blue' | 'green' | 'amber' | 'purple' | 'teal' | 'pink'): CSSProperties => ({
    ...qaStyle,
    background: `var(--c-${hue}-bg)`,
    borderColor: `color-mix(in srgb, var(--c-${hue}) 32%, var(--line))`,
    borderLeft: `3px solid var(--c-${hue})`,
  });
  const qaIcon: CSSProperties = {
    width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', fontSize: 17,
    background: 'var(--surface)',
  };
  const badgeStyle = (bg: string, color: string): CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700,
    padding: '2px 8px', borderRadius: 999, background: bg, color, whiteSpace: 'nowrap',
  });

  return (
    <Page
      title={t('day.title')}
      subtitle={d?.sessionDate
        ? <span style={{ color: 'var(--danger)', fontWeight: 700, letterSpacing: '.03em' }}>{formatDayDate(d.sessionDate)}</span>
        : undefined}
    >
      <div className="stack">
        <Greeting />
        {/* Compulsory load-out prompt (#25). A driver-cum-salesman must load his
            own van before taking orders or delivering; a plain driver waits for
            the office to load his. The server enforces both — this tells him why
            before he tries. */}
        {d?.started && !d?.closed && d?.van && d?.vanLoaded === false && (
          d?.canLoadOwnVan ? (
            /* A guided, do-this-now step (owner, 1 Nov) rather than a warning
               banner — it is the mandatory second thing in his morning, after
               starting the day and before selling or delivering off the van. */
            <div style={{
              background: 'var(--c-blue-bg, var(--bg))', border: '1px solid var(--line)',
              borderLeft: '3px solid var(--brand)', borderRadius: 'var(--radius)', padding: 14,
            }}>
              <div className="stack-2">
                <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                  <span style={{
                    width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center',
                    background: 'var(--brand)', color: 'var(--brand-ink)', fontWeight: 700, flex: '0 0 auto',
                  }}>2</span>
                  <div style={{ minWidth: 0 }}>
                    <div className="t-semi">Next: load out your van</div>
                    <div className="t-xs t-muted">
                      {d?.beat
                        ? `Load ${d.beat.beatName}'s stock before selling or delivering off the van.`
                        : 'Load your van before selling or delivering off it.'}
                    </div>
                  </div>
                </div>
                <Button block onClick={() => navigate('/load-out')}>Load out van</Button>
              </div>
            </div>
          ) : (
            <Banner tone="warn" title="Van not loaded yet">
              <span className="t-sm">
                The office has not loaded your van yet. You can deliver once the load-out is recorded.
              </span>
            </Banner>
          )
        )}
        {/* Mode: working alone vs paired. A salesperson pairs with a driver;
            a driver pairs with a salesperson — so the two options read for the
            role the person actually holds, while the underlying mode (solo/van)
            is the same. */}
        {/* The combined field agent needs no solo/paired choice — they are both
            roles — so the toggle is hidden for them and the full set of tiles is
            shown regardless. */}
        {!isFieldAgent && (
          <Segment
            value={mode}
            onChange={setMode}
            options={isDriver
              ? [
                { value: 'solo', label: 'Solo driver in Van' },
                { value: 'van', label: 'Driver in Van (with salesperson)' },
              ]
              : [
                { value: 'solo', label: 'Solo Sales Rep' },
                { value: 'van', label: 'Van Sales (With Driver)' },
              ]}
          />
        )}

        {/* Status */}
        <div className="row-wrap" style={{ gap: 8 }}>
          <Chip tone={d?.started ? 'ok' : 'neutral'}>
            {d?.started ? '🟢 GPS locked' : 'GPS locks when you start'}
          </Chip>
          {d?.sessionDate && <Chip tone="neutral">{d.sessionDate}</Chip>}
        </div>

        {/* Profile — salesperson (real) */}
        <Card>
          <div className="row" style={{ gap: 12, alignItems: 'center' }}>
            {photoKey ? (
              <AuthImage
                path={`/api/media/${encodeURIComponent(photoKey)}`}
                alt={user?.fullName ?? 'You'}
                style={{ width: 44, height: 44, borderRadius: 11, objectFit: 'cover', flex: '0 0 auto' }}
              />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 11, background: 'var(--brand)', color: 'var(--brand-ink)', display: 'grid', placeItems: 'center', fontWeight: 700, flex: '0 0 auto' }}>
                {initials(user?.fullName)}
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div className="t-semi">{user?.fullName ?? 'You'}</div>
              <div className="t-xs t-muted">{isFieldAgent ? 'Driver & Salesperson' : isDriver ? 'Driver' : 'Sales Representative'}</div>
              {d?.beat && <div className="t-xs">Beat: <strong>{d.beat.beatName}</strong></div>}
            </div>
          </div>
        </Card>

        {/* Two-gate delivery lock, shown to the driver so he knows WHY he cannot
            deliver yet — before he tries at a shop. Only when the gate is on. */}
        {d?.deliveryGate?.locked && (
          <Banner tone="warn" title="Deliveries are locked">
            <span className="t-sm">
              {!d.deliveryGate.accepted
                ? 'Accept today’s load to start delivering.'
                : 'You’ve accepted your load. Deliveries unlock once an admin or manager approves your beat & load.'}
            </span>
          </Banner>
        )}

        {/* Pairing — shown only in "van"/"with" mode, and SWAPPED by role:
            a salesperson rides with a DRIVER (picked from today's checked-in
            vans); a driver rides with a SALESPERSON (picked from the team). */}
        {mode === 'van' && !isDriver && (() => {
          const checkedIn = (vehicles.data?.items ?? []).filter((v) => v.taken_by && v.driver_name);
          const picked = checkedIn.find((v) => v.id === vanDriverVehicleId);
          return (
            <Card>
              <div className="stack-2">
                <Select
                  label="Driver you are riding with"
                  value={vanDriverVehicleId}
                  placeholder={checkedIn.length ? 'Choose the driver' : 'No driver checked in yet'}
                  options={checkedIn.map((v) => ({
                    value: v.id,
                    label: `${v.driver_name} · ${v.registration_number}`,
                  }))}
                  onChange={(e) => setVanDriverVehicleId(e.target.value)}
                />
                {picked && (
                  <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 11, background: '#0f7b46', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, flex: '0 0 auto' }}>
                      {initials(picked.driver_name ?? undefined)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="t-semi">{picked.driver_name}</div>
                      <div className="t-xs t-muted">Driver · {picked.registration_number}</div>
                      <div style={{ marginTop: 3 }}><Chip tone="ok">🟢 Checked-in</Chip></div>
                    </div>
                  </div>
                )}
                {!checkedIn.length && (
                  <div className="t-xs t-muted">
                    Drivers appear here once they start their day with a van. Pairing is assigned by admin.
                  </div>
                )}
              </div>
            </Card>
          );
        })()}

        {mode === 'van' && isDriver && !isFieldAgent && (() => {
          const team = salespeople.data?.items ?? [];
          const picked = team.find((s) => s.id === vanSalespersonId);
          return (
            <Card>
              <div className="stack-2">
                <Select
                  label="Salesperson you are riding with"
                  value={vanSalespersonId}
                  placeholder={team.length ? 'Choose the salesperson' : 'No salesperson available'}
                  options={team.map((s) => ({
                    value: s.id,
                    label: s.code ? `${s.name} · ${s.code}` : s.name,
                  }))}
                  onChange={(e) => setVanSalespersonId(e.target.value)}
                />
                {picked && (
                  <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 11, background: '#0f7b46', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, flex: '0 0 auto' }}>
                      {initials(picked.name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="t-semi">{picked.name}</div>
                      <div className="t-xs t-muted">Salesperson{picked.code ? ` · ${picked.code}` : ''}</div>
                    </div>
                  </div>
                )}
                {!team.length && (
                  <div className="t-xs t-muted">
                    No salesperson is available to pair with right now.
                  </div>
                )}
              </div>
            </Card>
          );
        })()}

        {/* Shown when there is no OPEN session — either the day has not started
            yet, or it was started and closed and may be started again (the
            same selfie + location + vehicle flow raises a fresh session). */}
        {(!d?.started || d?.closed) && (
          <Card>
            <div className="stack-3" style={{ padding: '12px 0' }}>
              <div className="t-center stack-1">
                <h2>{d?.closed ? 'Start a new session' : t('day.notStarted')}</h2>
                <p className="t-sm t-muted">
                  {d?.closed
                    ? 'Your day was closed. You can start again if you need to.'
                    : t('day.noTracking')}
                </p>
              </div>

              {/*
                THE VAN, BEFORE THE DAY OPENS.

                Earlier than the load sheet and a different question. A driver
                can be given two sheets in a day and can be handed a different
                van than the sheet was raised against: the sheet says which van
                the GOODS went into, and nothing said which van the MAN was
                driving. When they differ, van stock is reconciled against a
                vehicle he never had.

                A van somebody else already has open is shown and disabled
                rather than hidden — "where is JH01AB1234?" has an answer, and
                hiding it makes the list look wrong.
              */}
              {isDriver && (
                <Select
                  label="Vehicle"
                  required
                  value={vehicleId}
                  placeholder="Choose your vehicle"
                  options={(vehicles.data?.items ?? []).map((v) => ({
                    value: v.id,
                    // A van another has open is now SELECTABLE, not disabled: a
                    // crew shares one van (0077), so joining a van someone else
                    // started is exactly the case we want to allow. The label
                    // says who is already on it so the choice is deliberate.
                    label: v.taken_by
                      ? `${v.registration_number} — sharing with ${v.driver_name}`
                      : v.registration_number,
                  }))}
                  onChange={(e) => setVehicleId(e.target.value)}
                />
              )}

              <p className="t-xs t-muted t-center">
                Starting the day takes a photo of you and your location, once.
              </p>

              {/*
                A REAL CAMERA, not a file picker.

                `<input capture>` is a hint that an installed PWA and an
                Android WebView both ignore, which is why the gallery kept
                opening however the attribute was set. `facing="user"` because
                this is a selfie — the file input could not choose a lens at
                all.
              */}
              <CameraSheet
                open={cameraOpen}
                facing="user"
                title="Photo for attendance"
                compressOptions={{ maxDimension: 1280 }}
                onCapture={(img) => void finishStart(img)}
                onClose={() => setCameraOpen(false)}
              />

              {startBlock && (
                <Banner tone="warn" title="Cannot start the day yet">
                  <span className="t-sm">{startBlock}</span>
                </Banner>
              )}

              <Button
                block size="lg"
                busy={busy || locating}
                onClick={() => void startDay()}
              >
                {locating ? 'Finding your location…' : t('day.startDay')}
              </Button>
            </div>
          </Card>
        )}

        {/*
          "Your day is closed" banner removed: the Start-a-new-session card above
          already says the day was closed and can be restarted, so a separate
          green banner was redundant and read as a dead-end. (An auto-midnight
          close still surfaces through the session history, not here.)
        */}

        {/*
          The van, still loaded, after the day is closed.

          A warning rather than an "ok", because this is unfinished business:
          tomorrow's load-out starts from a vehicle stock figure that nobody
          has verified. The count itself is already filled in with what the
          system expects to be left, so this is one tap and a confirmation,
          not a stock-take.
        */}
        {pendingCount && (
          <Banner tone="warn" title={`${pendingCount.loadSheetNumber} is not counted back in`}>
            <div className="stack-2">
              <span className="t-sm">
                {pendingCount.skuCount} SKU still showing on the van. The count is
                already filled in with what should be left — check it and confirm.
              </span>
              <Button
                size="sm"
                onClick={() => navigate(`/routes/${pendingCount.loadSheetId}/load-in`)}
              >
                Count the van now
              </Button>
            </div>
          </Banner>
        )}

        {d?.started && !d.closed && (
          <Card>
            <div className="stack-2">
              <div className="row-between">
                <div>
                  <div className="t-semi">{t('day.startedAt', { time: timeShort(d.startedAt ?? '') })}</div>
                  {/* The location captured at start. It IS recorded (required to
                      start the day); show it and let them open the map, rather
                      than the old blanket "no location" line that read as a
                      failure. Falls back to that line only if truly absent. */}
                  {d.startLocation
                    ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(d.startLocation!.mapsUrl, '_blank', 'noopener')}
                      >
                        Location captured · open map
                        {d.startLocation.accuracyM != null ? ` · ±${Math.round(d.startLocation.accuracyM)} m` : ''}
                      </Button>
                    )
                    : <div className="t-xs t-muted">{t('day.noTracking')}</div>}
                </div>
                <Chip tone="ok">Open</Chip>
              </div>
              {/* Close day sits with the session it acts on. Beat is reachable
                  from its own card below and the nav, so it is not repeated here. */}
              <div className="row" style={{ justifyContent: 'flex-end' }}>
                <Button size="sm" onClick={() => setCloseOpen(true)}>{t('day.closeDay')}</Button>
              </div>
            </div>
          </Card>
        )}

        {/* Phase 3 — the van-start confirmation prompt. Shown only when the
            driver has an active load sheet he has not yet counted on his van.
            Non-blocking: the day is already started and the dashboard renders;
            tapping Confirm opens the per-SKU sheet below. */}
        {isDriver && d?.vanConfirm && (
          <Card>
            <div className="row-between" style={{ gap: 12, alignItems: 'center' }}>
              <div style={{ minWidth: 0 }}>
                <div className="t-xs t-muted">Van stock</div>
                <div className="t-semi t-clip">
                  Confirm {d.vanConfirm.skuCount} SKUs on your van
                </div>
                <div className="t-xs t-muted">
                  Load sheet {d.vanConfirm.loadSheetNumber}
                </div>
              </div>
              <Button
                size="sm"
                busy={vanConfirmLocating}
                onClick={() => void openVanConfirm()}
              >
                Confirm with photo
              </Button>
            </div>
          </Card>
        )}

        {/* Stock in Van — shown only when a van is actually assigned to this
            person (a load sheet exists / admin has detailed a vehicle), i.e.
            van.hasVan. A driver or field agent with no vehicle yet sees no card
            rather than an empty one; a salesperson's van-sales mode keeps its
            sample view. "Confirmed"/"Pending confirm" reflects the count state;
            categories match the billed-outlets reach list below. */}
        {((isDriver && van.data?.hasVan) || (!isDriver && mode === 'van')) && (
          <Card flush>
            <div className="card__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3>Stock in Van</h3>
              {van.data?.hasVan
                ? (van.data.allConfirmed
                  ? <Chip tone="ok">Confirmed</Chip>
                  : <Chip tone="warn">Pending confirm</Chip>)
                : <Chip tone="neutral">No van today</Chip>}
            </div>
            {/*
              Phase 6 — four-column table: Category · Loaded in Van · Order
              Delivered · Balance in Van, each cell in Pets & Pieces. The
              dashboard shows the four categories; the Stock tab itemises every
              product under them. Wrapped in an overflow-x scroller so four
              columns of "X Pets & Y Pieces" do not overflow a phone screen.
            */}
            <div style={{ overflowX: 'auto' }}>
              <table className="van-table" style={VAN_TABLE}>
                <thead>
                  <tr>
                    <th style={VAN_TH_LEFT}>Category</th>
                    <th style={VAN_TH}>Loaded in Van</th>
                    <th style={VAN_TH}>Order Delivered</th>
                    <th style={VAN_TH}>Balance in Van</th>
                  </tr>
                </thead>
                <tbody>
                  {STD_CATS.map((c) => {
                    const t = vanTotalsByCat.get(c.name) ?? {
                      loadedPets: 0, loadedPieces: 0, deliveredPets: 0,
                      deliveredPieces: 0, balancePets: 0, balancePieces: 0,
                    };
                    return (
                      <tr key={c.name}>
                        <td style={VAN_TD_LEFT}>{c.name}</td>
                        <td style={{ ...VAN_TD, color: 'var(--danger)' }}>
                          {petsPieces(t.loadedPets, t.loadedPieces)}
                        </td>
                        <td style={{ ...VAN_TD, color: 'var(--ink-2, #555)' }}>
                          {petsPieces(t.deliveredPets, t.deliveredPieces)}
                        </td>
                        <td style={{ ...VAN_TD, color: 'var(--ok)', fontWeight: 700 }}>
                          {petsPieces(t.balancePets, t.balancePieces)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Today's beat — and where a rep asks to move to another one. A pending
            request shows its own state; otherwise the allotted beat is shown
            with a "Request change" action. The Beat tab was removed from the
            nav, so this card is the one home for everything beat. */}
        {/* The beat NAME takes the full card width and the chip + action sit on
            their own row below. Keeping them on one row (row-between) squeezed a
            name like "Sodala Industrial Area" to "Sodal…" on a phone. */}
        {d?.beatChangePending ? (
          <Card>
            <div className="t-xs t-muted">{t('beat.title')}</div>
            <div className="t-semi">{d.beatChangePending.requestedBeat}</div>
            <div className="t-xs t-muted">Requested — awaiting admin approval</div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <Chip tone="warn">Pending approval</Chip>
            </div>
          </Card>
        ) : d?.beat ? (
          <Card>
            <div className="t-xs t-muted">{t('beat.title')}</div>
            <div className="t-semi">{d.beat.beatName}</div>
            <div className="t-xs t-muted">{t('beat.outlets', { n: d.beat.outletCount })}</div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <Chip tone="warn">Allotted by admin</Chip>
              <Button size="sm" variant="ghost" onClick={() => setBeatSheetOpen(true)}>
                Request change
              </Button>
            </div>
          </Card>
        ) : null}

        {/* Quick actions */}
        <div className="grid-2">
          {/* Opens the Orders list AT today's orders (requested): the count on this
              tile is today's bookings, so the tap now shows that exact list. A new
              order is still started from the "New order / Invoice" tile on the
              Orders screen it lands on. */}
          <button type="button" className="card" style={qaTile('blue')} onClick={() => navigate('/orders?view=today')}>
            <span style={qaIcon}>📦</span>
            <span className="t-sm t-semi">New order / Invoice</span>
            <span className="row" style={{ marginTop: 2 }}>
              <span style={badgeStyle('var(--accent-bg)', 'var(--accent)')}>
                Total orders: {p?.ordersBooked ?? 0}
              </span>
            </span>
          </button>
          {(isDriver || mode === 'van') && (
            <button type="button" className="card" style={qaTile('green')} onClick={() => navigate('/orders')}>
              <span style={qaIcon}>🚚</span><span className="t-sm t-semi">Spot Delivery to Customers</span>
            </button>
          )}
          {/* Stock in van — its own quick action since the bottom-bar Stock tab was
              replaced by Map. Keeps stock one tap from My Day for anyone on a van. */}
          {(isDriver || mode === 'van') && (
            <button type="button" className="card" style={qaTile('purple')} onClick={() => navigate('/van-stock')}>
              <span style={qaIcon}>📦</span>
              <span className="t-sm t-semi">Stock in van</span>
              <span className="t-xs t-muted" style={{ marginTop: 2 }}>What's on your vehicle</span>
            </button>
          )}
          {/* A driver-cum-salesman loads his own van. Shown once he has started
              on a vehicle; the load-out screen and the server both scope it to
              that van. A plain driver accepts an office load instead, so no tile. */}
          {isFieldAgent && d?.started && !d?.closed && (
            <button type="button" className="card" style={qaTile('teal')} onClick={() => navigate('/load-out')}>
              <span style={qaIcon}>📦</span>
              <span className="t-sm t-semi">Load out my van</span>
              <span className="t-xs t-muted" style={{ marginTop: 2 }}>Put stock on your van</span>
            </button>
          )}
          <button type="button" className="card" style={qaTile('amber')} onClick={() => navigate('/collections')}>
            <span style={qaIcon}>💵</span>
            <span className="t-sm t-semi">Collect cash / UPI</span>
            <span className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
              <span style={badgeStyle('var(--ok-bg)', 'var(--ok)')}>
                Cash: {money(p?.collectionsCashPaise ?? 0)}
              </span>
              <span style={badgeStyle('var(--c-blue-bg)', 'var(--accent)')}>
                UPI: {money(p?.collectionsUpiPaise ?? 0)}
              </span>
            </span>
          </button>
          <button type="button" className="card" style={qaTile('purple')} onClick={() => navigate('/customers')}>
            <span style={qaIcon}>📋</span>
            <span className="t-sm t-semi">Customer ledger</span>
            <span className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
              <span style={badgeStyle('var(--ok-bg)', 'var(--ok)')}>New customers {p?.customersAdded ?? 0}</span>
              <span style={badgeStyle('var(--line-2)', 'var(--ink-2)')}>Updated {p?.customersUpdated ?? 0}</span>
            </span>
          </button>

          {/* Delivery tiles — a driver's own pending round and what he has
              delivered today. Same look as the quick-action tiles; the pending
              one opens his "Orders Pending for Delivery" list, the delivered one
              his delivered-today list. */}
          {isDriver && (
            <button type="button" className="card" style={qaTile('amber')} onClick={() => navigate('/orders-to-deliver')}>
              <span style={qaIcon}>🚚</span>
              <span className="t-sm t-semi">Orders Pending for Delivery</span>
              <span className="row" style={{ marginTop: 2 }}>
                <span style={badgeStyle('var(--warn-bg, #fff5e6)', 'var(--warn, #c08400)')}>
                  Pending: {myPendingCount}
                </span>
              </span>
            </button>
          )}

          {isDriver && (
            <button type="button" className="card" style={qaTile('green')} onClick={() => navigate('/delivered-today')}>
              <span style={qaIcon}>✅</span>
              <span className="t-sm t-semi">Orders Delivered Today</span>
              <span className="row" style={{ marginTop: 2 }}>
                <span style={badgeStyle('var(--ok-bg)', 'var(--ok)')}>
                  Delivered: {myDeliveredCount}
                </span>
              </span>
            </button>
          )}
        </div>

        <div className="fieldgroup fieldgroup--teal" style={{ margin: '4px 0 0' }}>
          <h3 className="fieldgroup__title">Performance &amp; targets</h3>
        </div>
        <Segment
          value={win}
          onChange={setWin}
          options={[
            { value: 'month', label: 'Monthly target' },
            { value: 'today', label: "Today's target" },
          ]}
        />

        {/* Pets sold — the four category PET targets as full-width slide-bars,
            the same style as the admin Targets screen. Replaces the old
            badge-only summary tile. A driver with a single overall boxes target
            (no per-category) sees one "Overall" bar. Customers served and
            Collections stay as the tiles below. */}
        <Card className="stack-2">
          <div className="row-between">
            <span className="t-semi">Pets sold</span>
            <span className="t-sm t-muted">{petBlock?.period ?? ''}</span>
          </div>
          {petBars.map((r) => {
            const tone = petBarTone(r.pct, r.set);
            const pct = r.set ? Math.min(100, Math.max(0, r.pct)) : 0;
            return (
              <div key={r.name}>
                <div className="row-between" style={{ marginBottom: 4 }}>
                  <span className="t-sm t-muted">{r.name}</span>
                  <span className="t-sm">
                    <span className="t-semi">{r.achieved}</span>
                    <span className="t-muted"> / {r.set ? `${r.target} PETs` : '—'}</span>
                    {r.set && <span className={`t-semi t-${tone}`}> · {r.pct}%</span>}
                  </span>
                </div>
                <div className="meter" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                  <div className={`meter__fill meter__fill--${tone}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </Card>

        <div className="grid-2">
          <Tile
            tone="blue"
            label="Customers served"
            value={p?.outletsVisited ?? 0}
            meta={(
              <span className="row" style={{ gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
                <span style={badgeStyle('var(--ok-bg)', 'var(--ok)')}>New {p?.customersAdded ?? 0}</span>
                <span style={badgeStyle('var(--line-2)', 'var(--ink-2)')}>Served {p?.outletsVisited ?? 0}</span>
              </span>
            )}
          />
          {/* Collections — the same breakdown the admin dashboard shows:
              receipts count, Cash, UPI, Balance (owed on invoices delivered
              today) and Advance (over-tender held on account), scoped to this
              person's own collections. */}
          <Tile
            tone="green"
            label="Collections"
            value={money(p?.collectionsAmountPaise ?? 0)}
            onClick={() => navigate('/collections-today')}
            hint={`${p?.collectionsCount ?? 0} receipt(s) · view today's collections`}
            meta={(
              <span className="row" style={{ gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
                <span style={badgeStyle('var(--ok-bg)', 'var(--ok)')}>Cash {money(p?.collectionsCashPaise ?? 0)}</span>
                <span style={badgeStyle('var(--c-blue-bg)', 'var(--accent)')}>UPI {money(p?.collectionsUpiPaise ?? 0)}</span>
                <span style={badgeStyle(
                  (p?.collectionsBalancePaise ?? 0) > 0 ? 'var(--warn-bg, #fff5e6)' : 'var(--line-2)',
                  (p?.collectionsBalancePaise ?? 0) > 0 ? 'var(--warn, #c08400)' : 'var(--ink-2)',
                )}>
                  Balance {money(p?.collectionsBalancePaise ?? 0)}
                </span>
                <span style={badgeStyle('var(--c-blue-bg)', 'var(--accent)')}>
                  Advance {money(p?.collectionsAdvancePaise ?? 0)}
                </span>
              </span>
            )}
          />
          {mode === 'van' && (
            <Tile
              tone="purple"
              label="Van stock remaining"
              value="78"
              meta="Pets · sample"
              onClick={() => navigate('/van-stock')}
              hint="View full stock position"
            />
          )}
        </div>

        {/*
          TODAY BY CATEGORY.

          The breakdown a salesperson is actually measured on: targets are set
          per category, and until the Business Central category pull landed
          every product belonged to none, so this would have been one row
          reading "Uncategorised".

          Running rather than only at close, because at 2pm a zero against a
          category is still something he can act on. At 6pm it is a report.

          Quantities are BASE units — five cartons and seven loose bottles are
          127, not 12.
        */}
        {/* The PET target by category now renders as the full-width "Pets sold"
            slide-bar card above (admin Targets style), so the old 2-up tile grid
            that used to sit here was removed to avoid showing the same goal
            twice. */}

        {/* Billed-outlets reach — the same four categories as compact tiles.
            Real target where one is set; a sample goal (badged) where none. */}
        <div className="stack-2">
          <h3 style={{ margin: '4px 0 0' }}>Billed outlets reach · by category</h3>
          <div className="grid-2">
            {catReach.map((r) => {
              const pct = r.target > 0 ? Math.min(100, Math.round((r.billed / r.target) * 100)) : 0;
              return (
                <CatTile
                  key={r.name}
                  name={r.name}
                  color={r.color}
                  achieved={r.billed}
                  target={`${r.target} stores`}
                  pct={pct}
                  set
                  badge={r.sample ? 'Sample' : null}
                />
              );
            })}
          </div>
        </div>

        {/*
          The driver's version. Delivered and sold are printed apart because
          only one of them is his doing — see DriverBlock on the Targets
          screen for the same reasoning.
        */}
        {driverTarget.data?.driver && (
          <Card className="stack-2">
            <div className="row-between">
              <span className="t-semi">Today&apos;s boxes</span>
              <span className="t-sm t-muted">
                {driverTarget.data.driver.today.totalBoxes}
                {' / '}
                {driverTarget.data.driver.dailyTargetBoxes}
              </span>
            </div>
            <div className="row-wrap t-xs t-muted" style={{ gap: 10 }}>
              <span>Delivered <strong>{driverTarget.data.driver.today.delivered}</strong></span>
              <span>Sold off the van <strong>{driverTarget.data.driver.today.sold}</strong></span>
            </div>
            <div className="t-xs t-faint">
              {driverTarget.data.driver.monthly.totalBoxes} of{' '}
              {driverTarget.data.driver.targetBoxes} boxes this month
              {driverTarget.data.driver.monthly.percent !== null
                && ` · ${driverTarget.data.driver.monthly.percent}%`}
            </div>
          </Card>
        )}

      </div>

      <Sheet
        open={beatSheetOpen}
        onClose={() => setBeatSheetOpen(false)}
        title="Request a beat change"
        footer={(
          <>
            <Button variant="ghost" block onClick={() => setBeatSheetOpen(false)} disabled={beatBusy}>
              {t('app.cancel')}
            </Button>
            <Button block busy={beatBusy} onClick={() => void requestBeatChange()}>
              Send request
            </Button>
          </>
        )}
      >
        <div className="stack-2">
          <p className="t-sm t-muted">
            Your current beat is closed as soon as you send this. The new beat
            starts once an admin approves it.
          </p>
          <Select
            label="New beat"
            placeholder="Select a beat…"
            value={beatChoice}
            options={(beatOptions.data?.items ?? [])
              .filter((b) => b.id !== d?.beat?.beatId)
              .map((b) => ({ value: b.id, label: `${b.name} (${b.code})` }))}
            onChange={(e) => setBeatChoice(e.target.value)}
          />
          <TextArea
            label="Reason for change"
            required
            value={beatReason}
            onChange={(e) => setBeatReason(e.target.value)}
            placeholder="Why do you need to move to this beat?"
            maxLength={500}
          />
        </div>
      </Sheet>

      <ConfirmSheet
        open={closeOpen}
        onClose={() => setCloseOpen(false)}
        onConfirm={() => void closeDay()}
        busy={busy}
        title={t('day.closeDay')}
        confirmLabel={t('day.closeDay')}
        body={
          <div className="stack-2">
            <p className="t-sm t-muted">{t('day.closeConfirm')}</p>
            {p && (
              <Card>
                <div className="stack-2">
                  <Row label={t('day.outletsVisited')} value={String(p.outletsVisited)} />
                  <Row label={t('day.ordersBooked')} value={String(p.ordersBooked)} />
                  <Row label={t('day.orderValue')} value={money(p.orderValuePaise)} />
                  <Row label={t('day.collections')} value={money(p.collectionsAmountPaise)} />
                  <Row label={t('day.newCustomers')} value={String(p.customersAdded)} />
                </div>
              </Card>
            )}

            {/*
              Categories in the confirm sheet, not only after.

              "Close day - category wise summary also add in the current list"
              — and the list he is looking at when he decides to close is this
              one. Seeing "Water: 0" here is the last moment it can still be
              acted on; seeing it afterwards is a post-mortem.
            */}
            {(d?.byCategory?.length ?? 0) > 0 && (
              <Card>
                <div className="stack-2">
                  <div className="t-xs t-muted">Today by category</div>
                  {d!.byCategory!.map((row) => (
                    <Row
                      key={row.categoryId ?? row.category}
                      label={`${row.category} · ${row.quantity} pcs`}
                      value={row.display.value}
                    />
                  ))}
                </div>
              </Card>
            )}
            {/* CASH HANDOVER — both roles. Closing the day declares the handover
                note by note. The total is today's collection PLUS any balance
                carried from earlier days, not just today's figure. */}
            <Card className="stack-2">
              <div className="row-between">
                <span className="t-sm t-muted">Collection today</span>
                <span className="t-sm">{money(p?.collectionsCashPaise ?? 0)}</span>
              </div>
              {(cashMine.data?.amountPaise ?? 0) - (p?.collectionsCashPaise ?? 0) > 0 && (
                <div className="row-between">
                  <span className="t-sm t-muted">Balance brought forward</span>
                  <span className="t-sm">{money((cashMine.data?.amountPaise ?? 0) - (p?.collectionsCashPaise ?? 0))}</span>
                </div>
              )}
              <div className="row-between">
                <span className="t-semi">Total cash with you</span>
                <span className="t-semi">{money(cashMine.data?.amountPaise ?? 0)}</span>
              </div>

              {(cashMine.data?.amountPaise ?? 0) > 0 ? (
                <>
                  <NoteCounter
                    notes={closeNotes}
                    onChange={setCloseNotes}
                    title="Count the notes you are handing over"
                  />
                  <div className="row-between">
                    <span className="t-semi">You are handing over</span>
                    <span className="t-semi">{money(noteTotalPaise(closeNotes))}</span>
                  </div>
                  {noteTotalPaise(closeNotes) > 0
                    && noteTotalPaise(closeNotes) < (cashMine.data?.amountPaise ?? 0) && (
                    <Banner tone="warn">
                      <span className="t-sm">
                        {money((cashMine.data?.amountPaise ?? 0) - noteTotalPaise(closeNotes))} less
                        than you hold — that balance stays pending against you. Give the reason in
                        notes below.
                      </span>
                    </Banner>
                  )}
                </>
              ) : (
                <div className="t-xs t-muted">No cash to hand over.</div>
              )}
            </Card>

            {/* VAN STOCK — drivers only. Confirm what is left in the van matches
                the software before closing (the Stock in Van table above). */}
            {isDriver && (
              <Card className="stack-2">
                <div className="t-sm t-semi">Stock left in your van</div>
                {(van.data?.items?.length ?? 0) > 0 ? (
                  <div className="stack-2">
                    {van.data!.items.map((it) => (
                      <Row
                        key={it.productId}
                        label={it.category ?? it.productName}
                        value={`${it.remainingPets} Pets & ${it.remainingLoose} Pieces`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="t-xs t-muted">No stock showing on the van.</div>
                )}
                <Switch
                  checked={ackVan}
                  onChange={setAckVan}
                  label="I confirm the stock left in my van matches the above"
                />
              </Card>
            )}

            <TextArea
              label={t('visit.notes')}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              maxLength={500}
            />
          </div>
        }
      />

      {/*
        Phase 3 — the van-start confirmation sheet.

        One screen, one tap to confirm. The pet count defaults to what Admin
        loaded; the driver edits only the SKUs that don't match. Any line he
        changes shows an inline reason field — the server flags a variance
        without a reason, so the UX makes it one short step rather than two
        screens.

        The camera button is inside the sheet. Taking the photo is the last
        action, after the counts are correct — a camera opened first would
        force him to come back and recount if the photo failed.
      */}
      <Sheet
        open={vanConfirmOpen}
        onClose={() => setVanConfirmOpen(false)}
        title="Confirm van stock"
        footer={
          <Button
            block size="lg"
            busy={vanConfirmBusy}
            disabled={!vanPrefill.data || vanPrefill.data.items.length === 0}
            onClick={() => setVanConfirmCamera(true)}
          >
            Take photo &amp; confirm
          </Button>
        }
      >
        <div className="stack-2">
          {vanPrefill.loading && !vanPrefill.data && <Skeleton rows={4} height={64} />}
          {vanPrefill.data && (
            <>
              <p className="t-xs t-muted">
                {vanPrefill.data.loadSheetNumber} — {vanPrefill.data.items.length} SKUs.
                The number for each line is what Admin loaded; adjust only what
                differs on your van and say why.
              </p>
              {vanPrefill.data.items.map((i) => {
                const pack = i.packQty && i.packQty > 1 ? i.packQty : 1;
                const c = vanConfirmCounts[i.productId] ?? { pets: '', pieces: '' };
                const setC = (patch: Partial<{ pets: string; pieces: string }>) =>
                  setVanConfirmCounts((prev) => ({
                    ...prev,
                    [i.productId]: { pets: c.pets, pieces: c.pieces, ...patch },
                  }));
                // Everything compared in PIECES (exact), shown in pets + loose.
                const currentPieces = (Number(c.pets || 0) * pack) + Number(c.pieces || 0);
                const variance = currentPieces - i.loadedPieces;
                const loaded = splitPP(i.loadedPieces, i.packQty);
                const v = splitPP(Math.abs(variance), i.packQty);
                return (
                  <Card key={i.productId}>
                    <div className="stack-2">
                      <div style={{ minWidth: 0 }}>
                        <span className="t-sm t-semi t-clip">{i.productName}</span>
                        <span className="t-xs t-muted">
                          {i.skuCode}
                          {i.category ? ` · ${i.category}` : ''}
                          {pack > 1 ? ` · 1 pet = ${pack} pcs` : ''}
                        </span>
                      </div>
                      <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <TextInput
                            label="Pets"
                            inputMode="numeric"
                            value={c.pets}
                            onChange={(e) => setC({ pets: e.target.value.replace(/\D/g, '') })}
                          />
                        </div>
                        {pack > 1 && (
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <TextInput
                              label="Loose pieces"
                              inputMode="numeric"
                              value={c.pieces}
                              onChange={(e) => setC({ pieces: e.target.value.replace(/\D/g, '') })}
                            />
                          </div>
                        )}
                      </div>
                      <div className="row-between">
                        <span className="t-xs t-muted">
                          Loaded: <strong>{petsPieces(loaded.pets, loaded.loose)}</strong>
                        </span>
                        {variance !== 0 && (
                          <span className={`t-xs t-semi ${variance < 0 ? 't-danger' : 't-warn'}`}>
                            {variance < 0 ? 'Short' : 'Over'} {petsPieces(v.pets, v.loose)}
                          </span>
                        )}
                      </div>
                      {variance !== 0 && (
                        <TextInput
                          label="Reason for the difference"
                          placeholder="Why does the van differ from the sheet?"
                          value={vanConfirmReasons[i.productId] ?? ''}
                          onChange={(e) => setVanConfirmReasons((prev) => ({
                            ...prev, [i.productId]: e.target.value,
                          }))}
                          maxLength={500}
                        />
                      )}
                    </div>
                  </Card>
                );
              })}
              <Banner tone="info">
                <span className="t-xs">
                  Any difference without a reason is flagged for admin review.
                  You can still submit — admin will see the variance either way.
                </span>
              </Banner>
            </>
          )}
        </div>
      </Sheet>

      {/*
        One camera sheet for the van confirmation photo. Rear camera, since
        the picture faces the loaded van. Shared with no other capture flow:
        two open at once would fight over the device.
      */}
      <CameraSheet
        open={vanConfirmCamera}
        facing="environment"
        title="Photo of the loaded van"
        compressOptions={{ maxDimension: 1600, maxBytes: 2_500_000 }}
        onCapture={(img) => {
          setVanConfirmCamera(false);
          void submitVanConfirm(img);
        }}
        onClose={() => setVanConfirmCamera(false)}
      />
    </Page>
  );
}

/** "2026-10-20" → "TUE, 20 OCT 2026" for the dashboard date. */
function formatDayDate(iso: string): string {
  // Noon UTC + IST formatting, so the weekday and month are the IST calendar's,
  // not the viewing device's timezone. Day and year come from the ISO string
  // itself, which is already the IST business date.
  const dt = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(dt.getTime())) return iso;
  const [y, , d] = iso.split('-');
  const wk = dt.toLocaleString('en-GB', { weekday: 'short', timeZone: 'Asia/Kolkata' }).toUpperCase();
  const mon = dt.toLocaleString('en-GB', { month: 'short', timeZone: 'Asia/Kolkata' }).toUpperCase();
  return `${wk}, ${Number(d)} ${mon} ${y}`;
}

/** Up-to-two-letter initials from a name, for the profile avatar. */
function initials(name?: string): string {
  if (!name) return 'ME';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'ME';
  const a = parts[0]?.[0] ?? '';
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (a + b).toUpperCase() || 'ME';
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row-between t-sm">
      <span className="t-muted">{label}</span>
      <span className="t-semi">{value}</span>
    </div>
  );
}

/**
 * Target progress on all three measures.
 *
 * Cartons, value and customer count are shown together because the client set
 * targets on all three — showing only value would hide a salesperson hitting
 * revenue through two big customers while coverage collapsed.
 */
export function TargetCard({ block, label }: { block: TargetBlock; label: string }) {
  const t = useT();
  const first = block.targets[0];
  if (!first) return null;

  /**
   * The FIRST target's own numbers, not the day's headline.
   *
   * This card shows one target — targets[0] — and it used to pair that
   * target's goal with the block's total achievement. When the first target
   * was a category one, the bar filled from sales in every other category
   * too, and a salesperson ignoring the category he was being pushed on saw a
   * full bar. Each bar now reads from the row it belongs to.
   */
  const bars = [
    /*
      No cartons bar. Targets stopped carrying a carton figure on 3 Oct —
      "there is no need of carton section" — and a progress bar for a measure
      nobody can set would sit at 0% for ever and read as failure.

      The carton COUNT further up this screen stays: that is what he sold
      today, which is a fact, not a target.
    */
    /**
     * BOXES FIRST, because it is the measure now.
     *
     * 6 Oct: "Monthly target to be in Boxes and not in money". The rupee bar
     * stays below it for targets set before the change — those were real
     * instructions — and disappears on its own as those months pass.
     */
    {
      key: 'boxes',
      label: 'Boxes',
      target: first.target_boxes,
      achieved: first.achieved.boxes,
      percent: first.achievementPercent.boxes,
      display: (n: number) => String(n),
    },
    {
      key: 'value',
      label: t('target.value'),
      target: first.target_value_paise,
      achieved: first.achieved.valuePaise,
      percent: first.achievementPercent.value,
      display: (n: number) => money(n, { compact: true }),
    },
    {
      key: 'customers',
      label: t('target.customers'),
      target: first.target_customer_count,
      achieved: first.achieved.customerCount,
      percent: first.achievementPercent.customers,
      display: (n: number) => String(n),
    },
  ].filter((b) => b.target > 0);

  if (bars.length === 0) return null;

  return (
    <Card>
      <div className="stack-2">
        <div className="row-between">
          <span className="t-semi">
            {label}
            {/*
              Which slice of the day this card is about.
              Without it, "4 / 10 cartons" under a heading that says "Today"
              looks like the whole day and reads as a salesperson well behind,
              when he may have sold sixty cartons of everything else.
            */}
            {first.dimension_type !== 'overall' && (
              <span className="t-xs t-muted">
                {' · '}
                {first.category_name ?? first.area_name ?? first.store_type_name
                  ?? first.dimension_type.replace(/_/g, ' ')}
              </span>
            )}
          </span>
          <span className="t-xs t-muted">{block.period}</span>
        </div>
        {bars.map((bar) => {
          const pct = Math.min(100, bar.percent ?? 0);
          const tone = pct >= 100 ? 'var(--ok)' : pct >= 60 ? 'var(--warn)' : 'var(--danger)';
          return (
            <div key={bar.key}>
              <div className="row-between t-xs" style={{ marginBottom: 4 }}>
                <span className="t-muted">{bar.label}</span>
                <span className="t-semi">
                  {bar.display(bar.achieved)} / {bar.display(bar.target)}
                  {bar.percent !== null && bar.percent !== undefined && ` · ${bar.percent}%`}
                </span>
              </div>
              <div style={{ height: 6, background: 'var(--line-2)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: tone, borderRadius: 3 }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
